import {
  DEFAULT_EXPORT_SETTINGS,
  EXTENDED_TO_VISIBILITY_PROPS,
  LIST_VIEW_MODES,
  SOURCE_TYPES,
} from '../../../components/list-new/lib/list-constants'
import { get, isEmpty, isNull, map, pickBy, set, findKey } from 'lodash'
import { hasValue, parseFieldDependencies } from '../../../utils'
import { parseMultiColumnName } from '../../../utils/list'
import { localStoreGetters } from '../../mixins/local-store-module'
import { hamburgerGetters } from '../../mixins/hamburger-module'
import { paginationGetters } from '../../mixins/pagination-module'
import { parseJSON } from '../../../utils/json'

export default {
  ...localStoreGetters,
  ...hamburgerGetters,
  ...paginationGetters,
  // props - not changing
  buttonOverrides: (_, getters) => getters.extendedOptions.buttons,
  componentType: (state) => state.props.componentType,
  controllerName: (state) => state.props.controllerName,
  defaultEmpty: (state) => state.props.defaultEmpty,
  defaultView: (state) => get(state, 'props.extendedOptions.availableViews.defaultView', 'gridView'),
  disabled: (state) => state.props.disabled,
  docMTNList: (state) => state.props.docMTNList,
  exportSettings: (state) => {
    const hasNewSettings = state.props.extendedOptions.hasOwnProperty('exportSettings')
    if (hasNewSettings) {
      return get(state, 'props.extendedOptions.exportSettings')
    } else {
      const oldSetting = get(state, 'props.extendedOptions.showExportButton', true)
      return Object.assign(DEFAULT_EXPORT_SETTINGS, {
        showExportButton: oldSetting,
      })
    }
  },
  exportOriginalType: (_, getters) => get(getters.extendedOptions, 'exportOriginalType', false),
  extraColumns: (state) => get(state, 'props.extendedOptions.columns', []) || [],
  extraOptions: (state) => state.props.extraOptions,
  extendedOptions: (state) => state.props.extendedOptions,
  fieldName: (state) => state.props.fieldName,
  hamburgerSettings: (state) => state.props.extendedOptions,
  id: (_, getters) => {
    let id = !isEmpty(getters.mongoList)
      ? get(getters.mongoList, 'collection', '').toLowerCase()
      : getters.mongoId || getters.queryId || getters.processId || getters.rawSqlId || getters.panelId || null
    return [SOURCE_TYPES.SQL_CHOSEN].includes(getters.componentType) ? `${id}:${getters.fieldName}` : id
  },
  infiniteScroll: (state) => state.props.infiniteScroll,
  isCollapsed: (state) => state.props.isCollapsed,
  isInDashboard: (state) => state.props.isInDashboard,
  isInModal: (state) => state.props.isInModal,
  isMultiselect: (state) => state.props.multiSelect,
  isRelationshipList: (state) => state.props.isRelationshipList,
  isProcessList: (state) => !!state.props.processId,
  isVersionList: (state) => state.props.isVersionList,
  isSqlChosen: (state) => state.props.isSqlChosen,
  list: (state) => state.props.list || {},
  mongoId: (state) => state.props.mongoId,
  mongoList: (state) => state.props.mongoList,
  panelActions: (state) => state.props.processActions,
  pageSize: (state) => state.props.pageSize,
  panelId: (state) => state.props.panelId,
  panelUid: (state) => state.props.panelUid,
  popupTitle: (state) => state.props.popupTitle,
  queryId: (state) => state.props.queryId,
  rawSqlId: (state) => state.props.rawSqlId,
  rowStyle: (state) => state.props.extendedOptions.rowStyle,
  showActionButtons: (_, getters) => getters.visibilityState({ key: 'showActionButtons' }),
  showDetails: (state) => state.props.showDetails,
  showListOnStart: (state) => state.props.showListOnStart,
  sqlName: (state) => state.props.sqlName,
  subBlockUrl: (state) => state.props.subBlockUrl,
  extendedVisibilityProps() {
    return EXTENDED_TO_VISIBILITY_PROPS
  },
  rawSqlNamespace: (state) => state.props.rawSqlNamespace,
  sourceType: (state) => {
    if (state.props.mongoId) return SOURCE_TYPES.FORM
    if (state.props.mongoList) return SOURCE_TYPES.MONGO
    if (state.props.processId) return SOURCE_TYPES.PROCESS
    if (state.props.queryId) return SOURCE_TYPES.QUERY
    if (state.props.rawSqlId) return SOURCE_TYPES.RAW_SQL
    if (state.props.idWorkflow) return SOURCE_TYPES.WORKFLOW
  },
  idSource:
    (state) =>
    (path = null) =>
      state.props.mongoId ||
      state.props.mongoList ||
      state.props.processId ||
      state.props.queryId ||
      state.props.rawSqlId ||
      state.props.workflowId,
  // properties - changing
  aggregateColumn: (state) => state.listProperties.aggregateColumn,
  aggregateRow: (state) => state.listProperties.aggregateRow,
  aggregations: (state) => state.listProperties.aggregations,
  availableColumns: (state, getters) => {
    const columns = getters.columns.filter(
      (column) => getters.availableInList.includes(parseMultiColumnName(column.data))
    )
    return columns.length
      ? columns.reduce((accumulator, { title, data }) => {
        accumulator.push([ parseMultiColumnName(data), title || parseMultiColumnName(data) ])
        return accumulator
      }, [])
      : []
  },
  availableInDetailView: (state) => state.listProperties.availableInDetailView,
  availableInList: (state) => state.listProperties.availableInList,
  buttons: (state) => state.listProperties.buttons,
  collapsedButtons: (state) => state.listProperties.collapsedButtons,
  collectionOrId: (state, getters) => {
    const currentParameters = getters.currentParameters
    const id = currentParameters.formId ||
      currentParameters.queryId ||
      currentParameters.processId ||
      currentParameters.rawSqlId ||
      null
    const mongoList = parseJSON(get(currentParameters, 'mongoList', '{}'), getters.id)
    const collection = get(mongoList, 'collection', '').toLowerCase()
    return collection || id
  },
  columns: (state) => state.listProperties.columns,
  columnFilter: (state) => (data, defaultFilter) => get(state.listProperties.searchParams, data, defaultFilter),
  columnsWithPattern: (state, getters) => {
    const extraColumns = getters.extraColumns
    return extraColumns.filter((col) => col.pattern).map((col) => col.columnName)
  },
  confirmOptions: (state) => state.listProperties.confirmOptions,
  currentParameters: (state) => state.listProperties.currentParameters,
  defaultShowInList: (state) => state.listProperties.defaultShowInList,
  dynamicStyle: (state) => state.listProperties.dynamicStyle,
  exportableColumns: (state, getters) => (translate) => {
    let visCols = []
    let extraCols = []
    const columns = getters.columns

    if (!isNull(columns) && columns.length) {
      visCols = columns
        .filter((col) => col.default)
        .map((col) => {
          return {
            key: col.data,
            value: col.title,
          }
        })
    }

    const extraColumns = getters.extraColumns
    if (!isNull(extraColumns) && extraColumns.length) {
      extraCols = extraColumns
        .filter((col) => col.exportable)
        .map((col) => {
          return {
            key: col.columnName,
            value: col.label || col.columnName,
          }
        })
    }
    return [ ...visCols, ...extraCols ].reduce((acc, curr) => {
      if (!curr.value) return acc
      if (!acc.find((e) => e[0] === curr.key)) {
        acc.push([ curr.key, translate(curr.value) ])
      }
      return acc
    }, [])
  },
  fetchingListData: (state) => state.fetchingListData,
  groupActionsRunning: (state) => state.listProperties.groupActionsRunning,
  hiddenSearch: (state) => state.listProperties.hiddenSearch,
  //hamburger
  isHamburgerVisible: (_, getters) =>
    getters.areGroupActionsAvailable ||
    getters.showColumnSelector ||
    getters.isExportAvailable ||
    getters.showSavedFilter ||
    Object.keys(getters.views).length > 1 ||
    getters.showRowSelectionOption ||
    getters.showResetButton,
  showResetButton: (_, getters) => getters.visibilityState({ key: 'showResetButton' }),
  showRowSelectionOption: (_, getters) => getters.visibilityState({ key: 'showSelectionColumn' }),

  isListExpanded: (state) => state.listProperties.listVisibilityOptions.isExpanded,
  listVisibilityOptions: (state) => state.listProperties.listVisibilityOptions,
  orderParams: (_, getters) => {
    if (isEmpty(getters.orderState)) return []
    return map(getters.orderState, (value, key) => {
      return { column: key, dir: value }
    })
  },
  orderState: (state) => state.listProperties.orderState,
  primaryKey: (state) => state.listProperties.primaryKey,
  processActions: (state) => state.listProperties.processActions,
  processActionsButtons: (state) => state.listProperties.processActionsButtons,
  properties: (state) => state.listProperties,
  rowClasses: (state) => state.listProperties.rowClasses,
  rowDetails: (state) => state.listProperties.rowDetails,
  rowOptions: (state) => (key) => get(state.listProperties.rowDetails, [key, 'options'], {}),
  rowStyles: (state) => state.listProperties.rowStyles,
  searchBarIsCollapsed: (_, getters) => get(getters.extendedOptions, 'searchBarIsCollapsed'),
  columnClasses: (state) => state.listProperties.columnClasses,
  columnStyles: (state) => state.listProperties.columnStyles,
  cellClasses: (state) => state.listProperties.cellClasses,
  cellStyles: (state) => state.listProperties.cellStyles,
  searchParams: (state) => state.listProperties.searchParams,
  searchParamsComp: (state) => state.listProperties.searchParamsComp,
  selectedGroupAction: (state) => state.listProperties.selectedGroupAction,
  selectedQueueAction: (state) => state.listProperties.selectedQueueAction,
  selectedRow: (state) => state.listProperties.selectedRow,
  selectedRows: (state) => state.listProperties.selectedRows,
  selectedRowsFilter: (state) => state.listProperties.selectedRowsFilter,
  showInList: (state) => state.listProperties.showInList,
  sort: (state) => state.listProperties.sort,
  sqlChosenButtonProps: (state) => state.listProperties.sqlChosenButtonProps,
  storageKey: (state) => state.listProperties.storageKey,
  universalFilter: (state) => state.listProperties.universalFilter,
  viewMode: (state) => state.listProperties.viewMode,
  views: (_, getters) => pickBy(LIST_VIEW_MODES, (viewMode) => getters.extendedOptions.availableViews[viewMode.name]),
  visibilityState: (state) => ({ key }) =>
    get(state, ['listProperties', 'listVisibilityOptions', key], false),
  visibleColumns: (state) => state.listProperties.visibleColumns,
  expandedRows: (state) => state.listProperties.expandedRows,
  // listData
  listData: (state) => state.listData,
  recordsFiltered: (state) => state.recordsFiltered,
  recordsTotal: (state) => state.recordsTotal,
  listType: (state) => state.props.listType,
  expressionChain: (state) => state.expressionChain,
  fieldPropertiesMap: (state) => state.fieldPropertiesMap,
  expressionFieldByPath: (state) => (name) => {
    const field = get(state, ['fieldPropertiesMap', name])
    if (field) {
      return field
    }

    const fieldPathMap = get(state, 'fieldPathMap')
    if (fieldPathMap) {
      const mappedName = findKey(fieldPathMap, (val) => val === name)
      if (mappedName) {
        return get(state, ['fieldPropertiesMap', mappedName])
      }
    }
  },
  getFieldByName: (state) => (name) => state.fieldPropertiesMap[name],
  showAddRowButton: (state) => get(state, 'props.extraOptions.showAddRowButton', false),
  isSavable: (state) => get(state, 'isSavable', false),
  getListExpressionContextData: (state, getters) => (name) => {
    const field = getters.expressionFieldByPath(name)
    const expression = get(field, ['listAst', 'exp'], get(field, ['filterExp', 'exp'], get(field, ['listExp', 'exp'])))
    const expressionData = {}
    let dependencies = []
    if (expression) {
      dependencies = parseFieldDependencies(expression)
    }
    if (dependencies.length) {
      for (const { fieldName, prop } of dependencies) {
        const relatedField = getters.expressionFieldByPath(fieldName)
        const relatedFieldValue = get(relatedField, prop)
        set(expressionData, [relatedField.name, prop], hasValue(relatedFieldValue) ? relatedFieldValue : null)
      }
    }
    return isEmpty(expressionData) ? null : JSON.stringify(expressionData)
  },
  fieldPathMap: (state) => get(state, 'fieldPathMap', {}),
}
